import { useState, useEffect, useRef, useCallback } from 'react';

export function useIsBrowserVisible(): boolean {
  const [visible, setVisible] = useState(
    document.visibilityState === 'visible'
  );

  const onVisibilityStateChanged = useCallback(() => {
    setVisible(document.visibilityState === 'visible');
  }, [setVisible]);

  useEffect(() => {
    window.addEventListener('visibilitychange', onVisibilityStateChanged);
    return () => {
      window.removeEventListener('visibilitychange', onVisibilityStateChanged);
    };
  }, [onVisibilityStateChanged]);

  return visible;
}

export default function useNotificationsCount(
  showNotifications: boolean,
  pollInterval: number
): [number, () => void] {
  const [count, setCount] = useState<number>(0);
  const intervalRef = useRef<number>();

  const isVisible = useIsBrowserVisible();

  const fetchNotifications = useCallback(async () => {
    try {
      const resp = await window.fetch('/tr/v1/messages', {
        credentials: 'include',
        // If the endpoint returns a redirect response, it means the session is no longer valid.
        // in this case we want this to return an error.
        redirect: 'error',
        headers: {
          accept: 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error();
      }
      const body = await resp.json();
      setCount(body.total_count ?? 0);
    } catch (e) {
      setCount(0);
    }
  }, [setCount]);

  useEffect(() => {
    if (!showNotifications && pollInterval > 0 && isVisible) {
      intervalRef.current = window.setInterval(
        fetchNotifications,
        pollInterval
      );
    } else {
      window.clearInterval(intervalRef.current);
    }

    return () => {
      window.clearInterval(intervalRef.current);
    };
  }, [showNotifications, pollInterval, fetchNotifications, isVisible]);

  return [count, fetchNotifications];
}
